import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { CardEntityType } from "common247";
import { getEntityRoute } from "../ThemedEntityLink";
import { normalizeEntityType, type EntityType } from "@/constants/entity-icons";
import { entityCache } from "@/services/sync/entity-cache";

/**
 * Returns the canonical Ionicons glyph name for an entity category and optional tagType.
 *
 * @param {CardEntityType} entityType - The entity domain category.
 * @param {string} [tagType] - Optional tag domain type when entityType is 'tag'.
 * @returns {keyof typeof Ionicons.glyphMap} The Ionicons glyph identifier.
 */
export function getEntityIcon(
  entityType: CardEntityType,
  tagType?: string
): keyof typeof Ionicons.glyphMap {
  switch (entityType) {
    case "contact":
      return "person-circle-outline";
    case "group":
      return "people-outline";
    case "memory":
      return "sparkles-outline";
    case "media":
      return "images-outline";
    case "tag":
      switch ((tagType || "").toLowerCase()) {
        case "contact":
          return "person-circle-outline";
        case "group":
          return "people-outline";
        case "memory":
          return "sparkles-outline";
        case "media":
          return "images-outline";
        default:
          return "pricetag-outline";
      }
    case "share":
      return "paper-plane-outline";
    case "user":
      return "person-outline";
    case "message":
      return "mail-outline";
    case "place":
      return "location-outline";
    case "anchor":
      return "anchor-outline";
    default:
      return "cube-outline";
  }
}

/**
 * Navigates to the canonical route for a card entity if a valid ID is provided.
 *
 * @param {CardEntityType} entityType - The entity domain category.
 * @param {string} [id] - The primary identifier of the entity.
 * @param {string} [callerLabel] - Optional component label for diagnostic warning logs.
 */
export function navigateToCardEntity(
  entityType: CardEntityType,
  id?: string,
  callerLabel = "Card"
): void {
  if (!id || id === "unknown") return;
  const normType = normalizeEntityType(entityType as EntityType);
  const route = getEntityRoute(normType, id);
  if (!route) return;
  try {
    router.push(route as any);
  } catch (err) {
    console.warn(`[${callerLabel}] Navigation failed for "${route}":`, err);
  }
}

export interface IResolvedCardContact {
  id: string;
  contactId?: string;
  displayName: string;
  photoIconUrl?: string;
  photoUrl?: string;
  isOwner?: boolean;
}

export interface IResolvedCardGroup {
  id: string;
  groupId?: string;
  name: string;
  displayName?: string;
}

export interface IResolvedCardMemory {
  id: string;
  memoryId?: string;
  title: string;
  label?: string;
  favouriteRating?: number;
}

export interface IResolvedCardMedia {
  id: string;
  mediaId?: string;
  title: string;
  downloadUrl?: string;
  thumbnailUrl?: string;
  url?: string;
  isOwner?: boolean;
  isFiveStar?: boolean;
}

/**
 * Resolves an array of linked contacts from any card entity across relationship maps,
 * companion arrays, view models, and sharing summaries.
 *
 * @param {any} entity - The entity item (Media, Memory, Group, Contact, etc.).
 * @param {any[]} [availableContacts] - Optional array of loaded contacts to resolve display names and avatars.
 * @returns {IResolvedCardContact[]} Unified, deduplicated array of linked contacts.
 */
export function resolveCardLinkedContacts(
  entity: any,
  availableContacts?: any[]
): IResolvedCardContact[] {
  if (!entity || typeof entity !== "object") return [];

  const contactMap = new Map<string, IResolvedCardContact>();

  const addContact = (raw: any, explicitId?: string) => {
    if (!raw) return;
    const id = explicitId || raw.id || raw.contactId || (typeof raw === "string" ? raw : undefined);
    if (!id || typeof id !== "string" || id === "unknown") return;

    if (contactMap.has(id)) {
      const existing = contactMap.get(id)!;
      if ((!existing.displayName || existing.displayName === "Contact") && (raw.displayName || raw.name)) {
        existing.displayName = raw.displayName || raw.name;
      }
      if (!existing.photoIconUrl && (raw.photoIconUrl || raw.photoUrl)) {
        existing.photoIconUrl = raw.photoIconUrl || raw.photoUrl;
      }
      return;
    }

    const matched = availableContacts?.find((c: any) => c.id === id || c.contactId === id);
    const cached = entityCache.getCachedContact(id);
    const resolvedName =
      (typeof raw === "object" ? raw.displayName || raw.name : undefined) ||
      matched?.displayName ||
      matched?.name ||
      cached?.displayName ||
      (entity.contactNames && entity.contactNames[id]) ||
      "Contact";

    const resolvedPhoto =
      (typeof raw === "object" ? raw.photoIconUrl || raw.photoUrl : undefined) ||
      matched?.photoIconUrl ||
      matched?.photoUrl ||
      cached?.photoIconUrl;

    contactMap.set(id, {
      id,
      contactId: id,
      displayName: resolvedName,
      photoIconUrl: resolvedPhoto,
      isOwner: typeof raw === "object" ? Boolean(raw.isOwner) : (matched?.isOwner ?? cached?.isOwner ?? false),
    });
  };

  // 1. Hydrated contact view models
  if (Array.isArray(entity.contactItems)) {
    for (const c of entity.contactItems) addContact(c);
  }

  // 2. Structured relationship map Record<contactId, IEntityContactMeta>
  if (entity.contacts && typeof entity.contacts === "object") {
    if (Array.isArray(entity.contacts)) {
      for (const c of entity.contacts) addContact(c);
    } else {
      for (const [cId, meta] of Object.entries(entity.contacts)) {
        addContact(meta, cId);
      }
    }
  }

  // 3. Alternative contactsList property
  if (Array.isArray(entity.contactsList)) {
    for (const c of entity.contactsList) addContact(c);
  }

  // 4. Companion synchronized array contactIds[]
  if (Array.isArray(entity.contactIds)) {
    for (const cId of entity.contactIds) addContact(cId);
  }

  // 5. Sharing summary targets (sharedTo)
  if (Array.isArray(entity.sharedTo)) {
    for (const s of entity.sharedTo) {
      if (s && s.type === "contact") {
        addContact({ id: s.id, displayName: s.name || s.displayName, isOwner: false });
      }
    }
  }

  return Array.from(contactMap.values());
}

/**
 * Resolves an array of linked groups from any card entity across relationship maps,
 * companion arrays, view models, and sharing summaries.
 *
 * @param {any} entity - The entity item (Media, Memory, Group, Contact, etc.).
 * @param {any[]} [availableGroups] - Optional array of loaded groups to resolve display names.
 * @returns {IResolvedCardGroup[]} Unified, deduplicated array of linked groups.
 */
export function resolveCardLinkedGroups(
  entity: any,
  availableGroups?: any[]
): IResolvedCardGroup[] {
  if (!entity || typeof entity !== "object") return [];

  const groupMap = new Map<string, IResolvedCardGroup>();

  const addGroup = (raw: any, explicitId?: string) => {
    if (!raw) return;
    const id = explicitId || raw.id || raw.groupId || (typeof raw === "string" ? raw : undefined);
    if (!id || typeof id !== "string" || id === "unknown") return;

    if (groupMap.has(id)) {
      const existing = groupMap.get(id)!;
      if ((!existing.name || existing.name === "Group") && (raw.name || raw.displayName || raw.title)) {
        existing.name = raw.name || raw.displayName || raw.title;
        existing.displayName = existing.name;
      }
      return;
    }

    const matched = availableGroups?.find((g: any) => g.id === id || g.groupId === id);
    const cached = entityCache.getCachedGroup(id);
    const resolvedName =
      (typeof raw === "object" ? raw.name || raw.displayName || raw.title : undefined) ||
      matched?.name ||
      matched?.displayName ||
      matched?.title ||
      cached?.name ||
      (entity.groupNames && entity.groupNames[id]) ||
      "Group";

    groupMap.set(id, {
      id,
      groupId: id,
      name: resolvedName,
      displayName: resolvedName,
    });
  };

  // 1. Hydrated group view models
  if (Array.isArray(entity.groupItems)) {
    for (const g of entity.groupItems) addGroup(g);
  }

  // 2. Structured relationship map Record<groupId, IEntityGroupMeta>
  if (entity.groups && typeof entity.groups === "object") {
    if (Array.isArray(entity.groups)) {
      for (const g of entity.groups) addGroup(g);
    } else {
      for (const [gId, meta] of Object.entries(entity.groups)) {
        addGroup(meta, gId);
      }
    }
  }

  // 3. Child groups relationship map (for groups)
  if (entity.childGroups && typeof entity.childGroups === "object") {
    if (Array.isArray(entity.childGroups)) {
      for (const g of entity.childGroups) addGroup(g);
    } else {
      for (const [gId, meta] of Object.entries(entity.childGroups)) {
        addGroup(meta, gId);
      }
    }
  }

  // 4. Alternative groupsList property
  if (Array.isArray(entity.groupsList)) {
    for (const g of entity.groupsList) addGroup(g);
  }

  // 5. Companion synchronized array groupIds[]
  if (Array.isArray(entity.groupIds)) {
    for (const gId of entity.groupIds) addGroup(gId);
  }

  // 6. Child group IDs array childGroupIds[]
  if (Array.isArray(entity.childGroupIds)) {
    for (const gId of entity.childGroupIds) addGroup(gId);
  }

  // 7. Sharing summary targets (sharedTo)
  if (Array.isArray(entity.sharedTo)) {
    for (const s of entity.sharedTo) {
      if (s && s.type === "group") {
        addGroup({ id: s.id, name: s.name || s.displayName });
      }
    }
  }

  return Array.from(groupMap.values());
}

/**
 * Resolves an array of linked memories from any card entity across relationship maps,
 * companion arrays, view models, and titles.
 *
 * @param {any} entity - The entity item.
 * @returns {IResolvedCardMemory[]} Unified, deduplicated array of linked memories.
 */
export function resolveCardLinkedMemories(entity: any): IResolvedCardMemory[] {
  if (!entity || typeof entity !== "object") return [];

  const memoryMap = new Map<string, IResolvedCardMemory>();

  const addMemory = (raw: any, explicitId?: string, fallbackTitle?: string) => {
    if (!raw) return;
    const id = explicitId || raw.id || raw.memoryId || (typeof raw === "string" ? raw : undefined);
    if (!id || typeof id !== "string" || id === "unknown") return;

    if (memoryMap.has(id)) {
      const existing = memoryMap.get(id)!;
      if ((!existing.title || existing.title === "Memory") && (raw.title || raw.displayName || raw.label || fallbackTitle)) {
        existing.title = raw.title || raw.displayName || raw.label || fallbackTitle || "Memory";
        existing.label = existing.title;
      }
      return;
    }

    const cached = entityCache.getCachedMemory(id);
    const title =
      (typeof raw === "object" ? raw.title || raw.displayName || raw.label || raw.name : undefined) ||
      fallbackTitle ||
      cached?.title ||
      "Memory";

    memoryMap.set(id, {
      id,
      memoryId: id,
      title,
      label: title,
      favouriteRating: typeof raw === "object" ? raw.favouriteRating : cached?.isFiveStar ? 5 : undefined,
    });
  };

  if (Array.isArray(entity.memoryItems)) {
    for (const m of entity.memoryItems) addMemory(m);
  }

  if (entity.memories && typeof entity.memories === "object") {
    if (Array.isArray(entity.memories)) {
      for (const m of entity.memories) addMemory(m);
    } else {
      for (const [mId, meta] of Object.entries(entity.memories)) {
        addMemory(meta, mId);
      }
    }
  }

  if (Array.isArray(entity.memoriesList)) {
    for (const m of entity.memoriesList) addMemory(m);
  }

  if (Array.isArray(entity.memoryIds)) {
    entity.memoryIds.forEach((mId: string) => {
      addMemory(mId, mId);
    });
  }

  if (Array.isArray(entity.sharedTo)) {
    for (const s of entity.sharedTo) {
      if (s && s.type === "memory") {
        addMemory({ id: s.id, title: s.displayName || s.name });
      }
    }
  }

  return Array.from(memoryMap.values());
}

/**
 * Resolves an array of linked media from any card entity across relationship maps,
 * companion arrays, view models, and entity cache.
 *
 * @param {any} entity - The entity item (Memory, Group, Contact, Tag, etc.).
 * @param {any[]} [availableMedia] - Optional array of loaded media to resolve titles and thumbnails.
 * @returns {IResolvedCardMedia[]} Unified, deduplicated array of linked media.
 */
export function resolveCardLinkedMedia(
  entity: any,
  availableMedia?: any[]
): IResolvedCardMedia[] {
  if (!entity || typeof entity !== "object") return [];

  const mediaMap = new Map<string, IResolvedCardMedia>();

  const addMedia = (raw: any, explicitId?: string) => {
    if (!raw) return;
    const id = explicitId || raw.id || raw.mediaId || (typeof raw === "string" ? raw : undefined);
    if (!id || typeof id !== "string" || id === "unknown") return;

    if (mediaMap.has(id)) {
      const existing = mediaMap.get(id)!;
      if ((!existing.title || existing.title === "Media") && (raw.title || raw.name || raw.filename)) {
        existing.title = raw.title || raw.name || raw.filename;
      }
      if (!existing.thumbnailUrl && (raw.thumbnailUrl || raw.downloadUrl || raw.url)) {
        existing.thumbnailUrl = raw.thumbnailUrl || raw.downloadUrl || raw.url;
        existing.downloadUrl = existing.thumbnailUrl;
      }
      return;
    }

    const matched = availableMedia?.find((m: any) => m.id === id || m.mediaId === id);
    const cached = entityCache.getCachedMedia(id);

    const title =
      (typeof raw === "object" ? raw.title || raw.name || raw.filename : undefined) ||
      matched?.title ||
      matched?.filename ||
      cached?.title ||
      "Media";

    const thumbnailUrl =
      (typeof raw === "object" ? raw.thumbnailUrl || raw.downloadUrl || raw.url : undefined) ||
      matched?.thumbnailUrl ||
      matched?.downloadUrl ||
      matched?.url ||
      cached?.thumbnailUrl;

    const isOwner =
      typeof raw === "object" && typeof raw.isOwner === "boolean"
        ? raw.isOwner
        : matched?.isOwner ?? cached?.isOwner ?? false;

    const isFiveStar =
      (typeof raw === "object" ? raw.favouriteRating === 5 || raw.isFiveStar : false) ||
      matched?.favouriteRating === 5 ||
      cached?.isFiveStar ||
      false;

    mediaMap.set(id, {
      id,
      mediaId: id,
      title,
      downloadUrl: thumbnailUrl,
      thumbnailUrl,
      url: thumbnailUrl,
      isOwner,
      isFiveStar,
    });
  };

  // 1. Hydrated media items (mediaItems)
  if (Array.isArray(entity.mediaItems)) {
    for (const m of entity.mediaItems) addMedia(m);
  }

  // 2. Structured relationship map or array (media)
  if (entity.media && typeof entity.media === "object") {
    if (Array.isArray(entity.media)) {
      for (const m of entity.media) addMedia(m);
    } else {
      for (const [mId, meta] of Object.entries(entity.media)) {
        addMedia(meta, mId);
      }
    }
  }

  // 3. Alternative mediaList property
  if (Array.isArray(entity.mediaList)) {
    for (const m of entity.mediaList) addMedia(m);
  }

  // 4. Companion synchronized array mediaIds[]
  if (Array.isArray(entity.mediaIds)) {
    for (const mId of entity.mediaIds) addMedia(mId);
  }

  return Array.from(mediaMap.values());
}

export interface IResolvedCardPlace {
  id: string;
  placeId?: string;
  name: string;
  displayName?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  photoMediaId?: string;
}

/**
 * Resolves an array of linked places from any card entity across relationship maps,
 * companion arrays, view models, and direct references.
 *
 * @param {any} entity - The entity item (Memory, Media, Contact, Group, etc.).
 * @param {any[]} [availablePlaces] - Optional array of loaded places to resolve names and addresses.
 * @returns {IResolvedCardPlace[]} Unified, deduplicated array of linked places.
 */
export function resolveCardLinkedPlaces(
  entity: any,
  availablePlaces?: any[]
): IResolvedCardPlace[] {
  if (!entity || typeof entity !== "object") return [];

  const placeMap = new Map<string, IResolvedCardPlace>();

  const addPlace = (raw: any, explicitId?: string) => {
    if (!raw) return;
    const id = explicitId || raw.id || raw.placeId || (typeof raw === "string" ? raw : undefined);
    if (!id || typeof id !== "string" || id === "unknown") return;

    if (placeMap.has(id)) {
      const existing = placeMap.get(id)!;
      if ((!existing.name || existing.name === "Place") && (raw.name || raw.displayName)) {
        existing.name = raw.name || raw.displayName;
        existing.displayName = existing.name;
      }
      return;
    }

    const matched = availablePlaces?.find((p: any) => p.id === id || p.placeId === id);

    const name =
      (typeof raw === "object" ? raw.name || raw.displayName : undefined) ||
      matched?.name ||
      matched?.displayName ||
      "Place";

    placeMap.set(id, {
      id,
      placeId: id,
      name,
      displayName: name,
      address: (typeof raw === "object" && raw.address ? raw.address : undefined) || matched?.address,
      latitude: (typeof raw === "object" && raw.latitude !== undefined ? raw.latitude : undefined) ?? matched?.latitude,
      longitude: (typeof raw === "object" && raw.longitude !== undefined ? raw.longitude : undefined) ?? matched?.longitude,
      photoMediaId: (typeof raw === "object" && raw.photoMediaId ? raw.photoMediaId : undefined) || matched?.photoMediaId,
    });
  };

  // 0. Hydrated place items (placeItems)
  if (Array.isArray(entity.placeItems)) {
    for (const p of entity.placeItems) addPlace(p);
  }

  // 1. places relationship map or array
  if (entity.places && typeof entity.places === "object") {
    if (Array.isArray(entity.places)) {
      for (const p of entity.places) addPlace(p);
    } else {
      for (const [pId, meta] of Object.entries(entity.places)) {
        addPlace(meta, pId);
      }
    }
  }

  // 2. Alternative placesList property
  if (Array.isArray(entity.placesList)) {
    for (const p of entity.placesList) addPlace(p);
  }

  // 3. placeIds array
  if (Array.isArray(entity.placeIds)) {
    for (const pId of entity.placeIds) addPlace(pId, pId);
  }

  // 4. direct placeId
  if (entity.placeId) {
    addPlace(entity.place || entity.placeId, entity.placeId);
  }

  return Array.from(placeMap.values());
}

export interface IResolvedCardAnchor {
  id: string;
  anchorId?: string;
  name: string;
  displayName?: string;
  type?: string;
  photoMediaId?: string;
  photoUrl?: string;
}

/**
 * Resolves an array of linked anchors from any card entity across relationship maps,
 * companion arrays, view models, and direct references.
 *
 * @param {any} entity - The entity item (Memory, Media, Contact, Group, Place, Anchor, etc.).
 * @param {any[]} [availableAnchors] - Optional array of loaded anchors to resolve names and photos.
 * @returns {IResolvedCardAnchor[]} Unified, deduplicated array of linked anchors.
 */
export function resolveCardLinkedAnchors(
  entity: any,
  availableAnchors?: any[]
): IResolvedCardAnchor[] {
  if (!entity || typeof entity !== "object") return [];

  const anchorMap = new Map<string, IResolvedCardAnchor>();

  const addAnchor = (raw: any, explicitId?: string) => {
    if (!raw) return;
    const id = explicitId || raw.id || raw.anchorId || (typeof raw === "string" ? raw : undefined);
    if (!id || typeof id !== "string" || id === "unknown") return;

    if (anchorMap.has(id)) {
      const existing = anchorMap.get(id)!;
      if ((!existing.name || existing.name === "Anchor") && (raw.name || raw.displayName)) {
        existing.name = raw.name || raw.displayName;
        existing.displayName = existing.name;
      }
      return;
    }

    const matched = availableAnchors?.find((a: any) => a.id === id || a.anchorId === id);

    const name =
      (typeof raw === "object" ? raw.name || raw.displayName : undefined) ||
      matched?.name ||
      matched?.displayName ||
      "Anchor";

    anchorMap.set(id, {
      id,
      anchorId: id,
      name,
      displayName: name,
      type: (typeof raw === "object" && raw.type ? raw.type : undefined) || matched?.type,
      photoMediaId: (typeof raw === "object" && raw.photoMediaId ? raw.photoMediaId : undefined) || matched?.photoMediaId,
      photoUrl: (typeof raw === "object" && raw.photoUrl ? raw.photoUrl : undefined) || matched?.photoUrl,
    });
  };

  // 0. Hydrated anchor items (anchorItems)
  if (Array.isArray(entity.anchorItems)) {
    for (const a of entity.anchorItems) addAnchor(a);
  }

  // 1. anchors relationship map or array
  if (entity.anchors && typeof entity.anchors === "object") {
    if (Array.isArray(entity.anchors)) {
      for (const a of entity.anchors) addAnchor(a);
    } else {
      for (const [aId, meta] of Object.entries(entity.anchors)) {
        addAnchor(meta, aId);
      }
    }
  }

  // 2. Alternative anchorsList property
  if (Array.isArray(entity.anchorsList)) {
    for (const a of entity.anchorsList) addAnchor(a);
  }

  // 3. anchorIds array
  if (Array.isArray(entity.anchorIds)) {
    for (const aId of entity.anchorIds) addAnchor(aId, aId);
  }

  // 4. direct anchorId
  if (entity.anchorId) {
    addAnchor(entity.anchor || entity.anchorId, entity.anchorId);
  }

  return Array.from(anchorMap.values());
}



